import { Injectable } from '@angular/core';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { map, switchMap } from 'rxjs/operators';
import { DateTime } from 'luxon';

import { StatusPerDay } from '../../../../domain/models/status-per-day';
import { BckpConstants } from '../../../../bckp.constants';
import { VbrSuccessRateListDomainService } from '@apps/bckp/domain/veeam/vbr/vbr-successrate-list-domain.service';
import { CountServers, StatusPerRange, TotalCapacity, Variations } from '../../../dashboard/models';
import {
    LoadBackupVbrDashboardCountServersRequested, LoadBackupVbrDashboardCountServersSucceeded, LoadBackupVbrDashboardLastSessionRequested,
    LoadBackupVbrDashboardLastSessionSucceeded, LoadBackupVbrDashboardStatusPerRangeRequested, LoadBackupVbrDashboardStatusPerRangeSucceeded,
    LoadBackupVbrDashboardTicketingRequested,
    LoadBackupVbrDashboardTicketingSucceeded,
    LoadBackupVbrDashboardTotalCapacityRequested,
    LoadBackupVbrDashboardTotalCapacitySucceeded,
    LoadBackupVbrDashboardVariationsRequested,
    LoadBackupVbrDashboardVariationsSucceeded
} from './vbr-dashboard.actions';
import { Status, VolumetricVariation } from '@apps/bckp/domain/models';
import { VbrCapacityListDomainService } from '@apps/bckp/domain/veeam/vbr/vbr-capacity-list-domain.service';
import { RequestsSummaryCounter } from '@common/models';
import { RequestMiscDomainService } from '@apps/its2/domain';

@Injectable()
export class BckpVbrDashboardEffects
{
    constructor (
        private actions: Actions,
        private vbrSuccessRateListDomainService: VbrSuccessRateListDomainService,
        private vbrCapacityListDomainService: VbrCapacityListDomainService,
        private requestMiscDomainService: RequestMiscDomainService
    )
    { }

    loadBackupVbrDashboardStatusPerRangeRequested = createEffect(() => this.actions
        .pipe(
            ofType(LoadBackupVbrDashboardStatusPerRangeRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.vbrSuccessRateListDomainService.getStatusPerRange(params.since, params.until)
                    .pipe(
                        map((statusPerDay: StatusPerDay[]) =>
                            LoadBackupVbrDashboardStatusPerRangeSucceeded({
                                statusPerRange: {
                                    since: params.since,
                                    until: params.until,
                                    statusPerDay: statusPerDay,
                                    total: 0,
                                    expirationDate: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime })
                                } as StatusPerRange
                            })
                        )
                    )
            )
        )
    );

    loadBackupDashboardLastSessionRequested = createEffect(() => this.actions
        .pipe(
            ofType(LoadBackupVbrDashboardLastSessionRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.vbrSuccessRateListDomainService.getLastSession(params.since, params.until)
                    .pipe(
                        map((statuses: Status[]) =>
                            LoadBackupVbrDashboardLastSessionSucceeded({
                                lastSession: {
                                    since: params.since,
                                    until: params.until,
                                    statuses: statuses,
                                    total: statuses[0].counter + statuses[1].counter + statuses[2].counter,
                                    expirationDate: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime })
                                }
                            })
                        )
                    )
            )
        )
    );

    loadBackupVbrDashboardTotalServersRequested = createEffect(() => this.actions
        .pipe(
            ofType(LoadBackupVbrDashboardCountServersRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.vbrCapacityListDomainService.getCountServers(params.since, params.until)
                    .pipe(
                        map((countServers: number) =>
                            LoadBackupVbrDashboardCountServersSucceeded({
                                countServers: {
                                    since: params.since,
                                    until: params.until,
                                    validUntil: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime }),
                                    count: countServers
                                } as CountServers
                            })
                        )
                    )
            )
        )
    );

    loadBackupVbrDashboardTotalCapacityRequested = createEffect(() => this.actions
        .pipe(
            ofType(LoadBackupVbrDashboardTotalCapacityRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.vbrCapacityListDomainService.getTotalCapacity(params.since, params.until)
                    .pipe(
                        map((capacity: number) =>
                            LoadBackupVbrDashboardTotalCapacitySucceeded({
                                totalCapacity: {
                                    since: params.since,
                                    until: params.until,
                                    validUntil: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime }),
                                    capacity: capacity
                                } as TotalCapacity
                            })
                        )
                    )
            )
        )
    );

    loadBackupDashboardTicketingRequested = createEffect(() => this.actions
        .pipe(
            ofType(LoadBackupVbrDashboardTicketingRequested),
            switchMap(() =>
                this.requestMiscDomainService.getBckpSummary()
                    .pipe(
                        map((requestsSummaryCounter: RequestsSummaryCounter) =>
                            LoadBackupVbrDashboardTicketingSucceeded({
                                ticketing: requestsSummaryCounter
                            })
                        )
                    )
            )
        )
    );

    loadBackupVbrDashboardVariationsRequested = createEffect(() => this.actions
        .pipe(
            ofType(LoadBackupVbrDashboardVariationsRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.vbrCapacityListDomainService.getVolumetricVariation(params.since, params.until)
                    .pipe(
                        map((volumetricVariations: VolumetricVariation[]) =>
                            LoadBackupVbrDashboardVariationsSucceeded({
                                variations: {
                                    since: params.since,
                                    until: params.until,
                                    validUntil: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime }),
                                    volumetricVariations: [...volumetricVariations]
                                } as Variations
                            })
                        )
                    )
            )
        )
    );
}
